import { useEffect, useRef } from 'react';
import { useAuditMode } from '../../context/AuditMode';

/**
 * EG-63 — cada renderer de tipo (Questionnaire, *Certification, DiagnosisType,
 * PeriodicType) chama isto em modo auditoria para publicar o progresso e as
 * ações (download, "ver como respondente") no `AuditHeader` único. Fora da
 * auditoria (`useAuditMode()` devolve null) é no-op.
 *
 * BUG CORRIGIDO: os callbacks (`onDownloadResponses` etc.) chegam como funções
 * inline do chamador — uma identidade NOVA a cada render. Se o efeito abaixo
 * dependesse delas diretamente, republicava a cada render, mudava o valor do
 * contexto, re-renderizava o chamador (que consome o contexto via
 * `useIsAudit`), que criava funções novas de novo — loop de render que
 * travava a tela inteira (evidência, chat, nota, GRI, nada respondia).
 * Correção: os callbacks passam por um wrapper ESTÁVEL (identidade fixa,
 * via ref) que sempre chama a versão mais recente; o efeito só depende dos
 * valores PRIMITIVOS (contadores, flags), nunca de função.
 */
export default function useAuditHeaderData(data) {
 const auditMode = useAuditMode();
 const setHeaderData = auditMode?.setHeaderData;

 const dataRef = useRef(data);
 dataRef.current = data;

 // Criados uma vez só (useRef, não useState/useMemo) — a identidade nunca
 // muda entre renders, então não entra na dependência do efeito abaixo.
 const stableCallbacksRef = useRef({
  onViewAsRespondent: (...args) => dataRef.current?.onViewAsRespondent?.(...args),
  onDownloadResponses: (...args) => dataRef.current?.onDownloadResponses?.(...args),
  onDownloadEvidence: (...args) => dataRef.current?.onDownloadEvidence?.(...args),
 });

 // Só os valores PRIMITIVOS entram na dependência (nunca uma função) — é
 // isso que impede o loop descrito acima.
 const temDados = !!data;
 const { answered, total, requiredAnswered, requiredTotal, indicatorsAnswered,
  indicatorsTotal, downloadingResponses, downloadingEvidence, evidenceDisabled } =
  data || {};

 useEffect(() => {
  if (!setHeaderData) return undefined;
  if (!dataRef.current) {
   setHeaderData(null);
   return undefined;
  }
  setHeaderData({
   ...dataRef.current,
   ...stableCallbacksRef.current,
  });
  return () => setHeaderData(null);
 }, [
  setHeaderData,
  temDados,
  answered,
  total,
  requiredAnswered,
  requiredTotal,
  indicatorsAnswered,
  indicatorsTotal,
  downloadingResponses,
  downloadingEvidence,
  evidenceDisabled,
 ]);
}

/** Ativo? (o renderer usa pra esconder o próprio sub-header) */
export function useIsAudit() {
 return !!useAuditMode()?.hideInlineHeader;
}
